import { recordFlags, isResearchRecord } from "./collection.js";
function csvCell(value) {
  const text = value == null ? "" : (typeof value === "string" ? value : (typeof value === "object" ? JSON.stringify(value) : String(value)));
  return `"${String(text).replaceAll('"', '""')}"`;
}

function parseInterviewPayload(document) {
  if (document.payload_json && typeof document.payload_json === "object") return document.payload_json;
  try { return JSON.parse(document.payload_json || "{}"); }
  catch { return {}; }
}

export function wideInterviewRow(document, researchers = []) {
  const payload = parseInterviewPayload(document);
  const responses = payload.responses || {};
  const profile = responses.profile || {};
  const mobility = responses.mobility || {};
  const fieldContext = responses.field_context || {};
  const location = payload.location || {};
  const researcher = researchers.find(item => item.id === document.synced_by);
  const row = {
    interview_id: document.interview_id,
    ...recordFlags(document),
    analysis_eligible: isResearchRecord(document) && !document.deletion_requested,
    recent_participation: responses.recent_participation || "",
    origin_neighborhood_category: mobility.origin_neighborhood_category || "",
    origin_neighborhood_other: mobility.origin_neighborhood_other || "",
    respondent_id: document.respondent_id || payload.respondentId || document.interview_id,
    revision: document.revision,
    phase: document.phase,
    researcher_user_id: document.synced_by,
    researcher_email: document.researcher_email || researcher?.email || "",
    researcher_name: document.researcher_name || researcher?.name || "",
    interviewer_code: document.interviewer_code,
    device_id: document.device_id,
    started_at: document.started_at,
    finished_at: document.finished_at,
    duration_seconds: document.duration_seconds,
    timezone_offset_minutes: payload.timezoneOffset ?? "",
    location_status: location.status || "",
    latitude: location.latitude ?? "",
    longitude: location.longitude ?? "",
    accuracy_m: location.accuracy_m ?? "",
    location_captured_at: location.captured_at || "",
    gender_identity: document.gender_identity || profile.gender_identity || "",
    age_years: document.age_years ?? profile.age_years ?? "",
    metro_habit: document.metro_habit || mobility.metro_habit || "",
    origin_city: document.origin_city || mobility.origin_city || "",
    origin_neighborhood: document.origin_neighborhood || mobility.origin_neighborhood || "",
    neighborhood_catalog_version: document.neighborhood_catalog_version ?? mobility.neighborhood_catalog_version ?? "",
    treatment_group: document.treatment_group || fieldContext.treatment_group || "",
    TRAT_i: document.trat_i ?? fieldContext.TRAT_i ?? "",
    approach_type: document.approach_type || fieldContext.approach_type || "",
    line_or_location: document.line_or_location || fieldContext.line_or_location || "",
    reference_station: document.reference_station || fieldContext.reference_station || "",
    pitaguari_serves_stop: document.pitaguari_serves_stop ?? fieldContext.pitaguari_serves_stop ?? "",
    block_id: document.block_id,
    deletion_requested: Boolean(document.deletion_requested),
    deletion_reason: document.deletion_reason || "",
    deletion_requested_at: document.deletion_requested_at || "",
    experiment_id: document.experiment_id,
    experiment_version: document.experiment_version,
    questionnaire_version: document.questionnaire_version,
    app_version: document.app_version,
    received_at: document.created_at || document.$createdAt || "",
    last_updated_at: document.updated_at || document.$updatedAt || ""
  };

  (payload.designSnapshot?.tasks || []).forEach((task, taskIndex) => {
    const prefix = `choice_${taskIndex + 1}`;
    const choice = responses.choices?.[task.task_id] || {};
    row[`${prefix}_task_id`] = task.task_id;
    row[`${prefix}_scenario_id`] = task.scenario_id;
    row[`${prefix}_selected_alternative`] = choice.selected_alternative || "";
    row[`${prefix}_selected_label`] = choice.selected_label || "";
    row[`${prefix}_answered_at`] = choice.answered_at || "";
    row[`${prefix}_displayed_alternatives`] = (choice.displayed_alternatives || task.display_order || task.alternatives.map(item => item.id)).join("|");
    row[`${prefix}_presentation_version`] = choice.presentation_version || task.presentation_version || 1;
    (task.alternatives || []).forEach(alternative => {
      (alternative.attributes || []).forEach(attribute => {
        row[`${prefix}_${alternative.id}_${attribute.id}`] = attribute.value;
      });
    });
  });

  for (const answers of Object.values(responses.psychometrics || {})) {
    for (const [id, value] of Object.entries(answers || {})) row[id] = value;
  }
  return row;
}

export function buildWideCsv(documents, researchers = []) {
  const rows = documents.map(document => wideInterviewRow(document, researchers));
  const fields = [...new Set(rows.flatMap(row => Object.keys(row)))];
  const lines = [fields.map(csvCell).join(",")];
  rows.forEach(row => lines.push(fields.map(field => csvCell(row[field])).join(",")));
  return `\ufeff${lines.join("\r\n")}`;
}

export function choiceLongRows(document, researchers = []) {
  const payload = parseInterviewPayload(document);
  const base = wideInterviewRow(document, researchers);
  const responses = payload.responses || {};
  const psychometrics = Object.fromEntries(
    Object.values(responses.psychometrics || {}).flatMap(answers => Object.entries(answers || {}))
  );
  return (payload.designSnapshot?.tasks || []).map((task, taskIndex) => {
    const choice = responses.choices?.[task.task_id] || {};
    const row = {
      respondent_id: base.respondent_id,
      interview_id: base.interview_id,
      is_test: base.is_test,
      duplicate_attempt: base.duplicate_attempt,
      analysis_eligible: base.analysis_eligible,
      phase: base.phase,
      experiment_version: base.experiment_version,
      questionnaire_version: base.questionnaire_version,
      block_id: base.block_id,
      task_index: taskIndex + 1,
      task_id: task.task_id,
      scenario_id: task.scenario_id,
      displayed_alternatives: (choice.displayed_alternatives || task.display_order || task.alternatives.map(item => item.id)).join("|"),
      presentation_version: choice.presentation_version || task.presentation_version || 1,
      selected_alternative: choice.selected_alternative || "",
      choice_metro: !choice.selected_alternative ? "" : (choice.selected_alternative === "metro" ? 1 : 0),
      answered_at: choice.answered_at || "",
      treatment_group: base.treatment_group,
      TRAT_i: base.TRAT_i,
      metro_habit: base.metro_habit,
      gender_identity: base.gender_identity,
      age_years: base.age_years,
      origin_city: base.origin_city,
      origin_neighborhood: base.origin_neighborhood,
      ...psychometrics
    };
    for (const alternative of task.alternatives || []) {
      for (const attribute of alternative.attributes || []) {
        row[`${alternative.id}_${attribute.id}`] = attribute.value;
      }
    }
    return row;
  });
}

export function buildChoiceLongCsv(documents, researchers = []) {
  const rows = documents.flatMap(document => choiceLongRows(document, researchers));
  const fields = [...new Set(rows.flatMap(row => Object.keys(row)))];
  const lines = [fields.map(csvCell).join(",")];
  rows.forEach(row => lines.push(fields.map(field => csvCell(row[field])).join(",")));
  return `\ufeff${lines.join("\r\n")}`;
}
